import { Menu, MenuItem } from '@mui/material'
import { useState } from 'react'
import SvgExport from '#/assets/images/icons/cloud_download.svg'
import AuthLock from '#/components/AuthLock'
import Badge from '#/components/Badge'
import Button from '#/components/Button'
import Modal from '#/components/Modal'
import LoadingSpinner from '#/components/OverlaySpinner/Spinners/Hexagon'
import { PDF_FULL_PROPERTY_PAGE, PDF_PROPERTY_PAGE } from '#/consts/url'
import type { SectionsConfig } from '#/containers/PDF/FullProperty/components/Report/types'
import CustomizeReport from '#/containers/ReportCustomize'
import OnePageReportCustomize from '#/containers/ReportCustomize/OnePage'
import { logEvent } from '#/services/analytics'
import { ShareLink } from '#/store/actions/user'
import { RequestReport } from '#/store/actions/workspace'
import cn from '#/utils/classnames'
import { viewUrl } from '#/utils/properties'

type Props = {
  className: string
  asyncStatus: TopHap.GlobalState['status']
  property: TopHap.Property
  user: TopHap.UserInfo
  requestReport: typeof RequestReport
  shareLink: typeof ShareLink
}

const ReportAction: React.FC<Props> = ({
  asyncStatus,
  className,
  property,
  user,
  requestReport,
  shareLink
}) => {
  const [menuReportEl, setMenuReportEl] = useState<Element | null>(null)
  const [modal, showModal] = useState<TopHap.ReportType | null>(null)

  function closeModal() {
    showModal(null)
  }

  function handleConfigChange(config: SectionsConfig) {
    closeModal()
    onFullPropertyReport(config)
  }

  function handleCloseReportMenu() {
    setMenuReportEl(null)
  }

  async function onPropertyReport() {
    handleCloseReportMenu()

    showModal('PROPERTY_ONE_PAGE')
  }

  async function handleReportDownload() {
    closeModal()

    logEvent('select_content', {
      content_type: 'property_detail',
      item_id: 'report',
      report_type: 'property_report',
      value: property.id,
      address: property.address.FullAddress
    })

    let qrLink
    await shareLink({
      type: 'Property',
      entityId: property.id,
      url: window.origin + viewUrl(property.id, property.address),
      copy: false,
      callback: shareLink => { qrLink = shareLink }
    })

    requestReport({
      payload: {
        frontendUrl: `${window.origin}${PDF_PROPERTY_PAGE.replace('[id]', property.id)}`,
        kind: 'PROPERTY_ONE_PAGE',
        propertyId: property.id,
        clientPayload: { qrLink }
      }
    })
  }

  async function onFullPropertyReport(config: SectionsConfig) {
    logEvent('select_content', {
      content_type: 'property_detail',
      item_id: 'report',
      report_type: 'full_property_report',
      value: property.id,
      address: property.address.FullAddress
    })

    let qrLink
    await shareLink({
      type: 'Property',
      entityId: property.id,
      url: window.origin + viewUrl(property.id, property.address),
      copy: false,
      callback: shareLink => { qrLink = shareLink }
    })

    requestReport({
      payload: {
        frontendUrl: `${window.origin}${PDF_FULL_PROPERTY_PAGE.replace('[id]', property.id)}`,
        kind: 'PROPERTY_FULL',
        propertyId: property.id,
        clientPayload: {
          sectionsConfig: config,
          qrLink
        }
      }
    })
  }

  function onCustomizeFullPropertyReport() {
    handleCloseReportMenu()

    showModal('PROPERTY_FULL')
  }

  return (
    <div className='relative z-40 inline-block align-top'>
      <Button
        className={cn(className, 'overflow-visible')}
        onClick={(ev: React.MouseEvent) => setMenuReportEl(ev.currentTarget)}
      >
        <Badge
          className='absolute -right-1.5 -top-2'
          role='pro'
          text='new'
        />
        {(asyncStatus[RequestReport.type] === 'request') ? (
          <span className='pb-0'>
            <LoadingSpinner size={32} />
          </span>
        ) : (
          <SvgExport className='mb-2 h-6' />
        )}
        <span>Reports</span>
      </Button>
      <Menu
        anchorEl={menuReportEl}
        classes={{ paper: 'divide-y divide-gray-200' }}
        open={Boolean(menuReportEl)}
        onClose={handleCloseReportMenu}
      >
        <MenuItem onClick={onPropertyReport}>
          <AuthLock
            className='items-start px-2'
            event='report'
            onlyIcon
            requireRole='pro'
            transparent
          />
          <span className='py-1 pl-5 first:pl-0'>Property Report</span>
        </MenuItem>
        <MenuItem onClick={onCustomizeFullPropertyReport}>
          <AuthLock
            className='items-start px-2'
            event='report'
            onlyIcon
            requireRole='pro'
            transparent
          />
          <span className='py-1 pl-5 first:pl-0'>Full Property Report</span>
        </MenuItem>
      </Menu>
      {modal && (
        <Modal
          visible={Boolean(modal)}
          onClose={closeModal}
        >
          {modal === 'PROPERTY_FULL' && (
            <CustomizeReport
              property={property}
              reportType='PROPERTY_FULL'
              user={user}
              onChangeConfig={handleConfigChange}
            />
          )}
          {modal === 'PROPERTY_ONE_PAGE' && (
            <OnePageReportCustomize
              property={property}
              user={user}
              onDownload={handleReportDownload}
            />
          )}
        </Modal>
      )}
    </div>
  )
}

export default ReportAction
